// N8N Webhook Service
// Handles communication with N8N webhooks for email processing

import { supabase } from '@/lib/supabase';
import { getEmailConnections, updateLastSyncDate, markHistoricalSyncComplete } from '@/lib/database/email-connections-helpers';

import { getWebhookProxyUrl, logCurrentConfig, getOAuthRedirectUri, getWebhookUrl } from '@/utils/config';
import backendApiService from '@/services/backendApi';

// Helper function to create Basic Auth header for N8N webhooks
const createBasicAuthHeader = (secret) => {
  if (!secret) return {};
  // N8N webhooks expect Basic authentication with the secret as the password
  const encoded = btoa(`webhook:${secret}`);
  return { 'Authorization': `Basic ${encoded}` };
};

// Add this helper function
const getWebhookUrlForService = (useTest = false) => {
  // Always use direct N8N URL (both development and production)
  const webhookUrl = getWebhookUrl(useTest);
  console.log(`🚀 Using ${useTest ? 'test' : 'production'} webhook URL:`, webhookUrl);
  return webhookUrl;
};

/**
 * N8N Webhook Service
 * Manages communication with N8N webhooks for email processing
 */
class N8NWebhookService {
  constructor(useTest = false) {
    this.useTest = useTest;
    this.webhookUrl = getWebhookUrlForService(useTest);
    this.webhookSecret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
    this.webhookToken = import.meta.env.VITE_N8N_WEBHOOK_TOKEN;
    this.timeout = parseInt(import.meta.env.VITE_WEBHOOK_TIMEOUT) || 30000;
    
    console.log('WebhookService: Using URL:', this.webhookUrl);
    console.log('WebhookService: Environment:', import.meta.env.DEV ? 'Development' : 'Production');
    console.log('WebhookService: Auth method:', this.webhookToken ? 'X-API-Key' : 'X-Webhook-Secret');
  }

  /**
   * Check if webhook is properly configured
   */
  isConfigured() {
    return !!(this.webhookUrl && this.webhookUrl !== 'your_n8n_webhook_url_here');
  }

  /**
   * Send request to N8N webhook
   * @param {Object} payload - Data to send to webhook
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Webhook response
   */
  async sendWebhookRequest(payload, options = {}) {
    if (!this.isConfigured()) {
      throw new Error('N8N webhook is not configured. Please check your environment variables.');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };

      // Use Basic authentication for N8N webhook
      if (this.webhookSecret) {
        Object.assign(headers, createBasicAuthHeader(this.webhookSecret));
        console.log('WebhookService: Using Basic authentication');
      } else {
        console.warn('WebhookService: No webhook secret configured');
      }

      console.log('WebhookService: Sending request to:', this.webhookUrl);
      console.log('WebhookService: Headers:', headers);
      console.log('WebhookService: Payload:', payload);

      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('WebhookService: Request failed:', response.status, response.statusText, errorText);
        
        // Handle specific error types
        if (response.status === 401) {
          console.error('WebhookService: Authentication failed (401). Check X-API-Key token.');
          throw new Error(`Authentication failed: Invalid X-API-Key token. Check VITE_N8N_WEBHOOK_TOKEN configuration.`);
        } else if (response.status === 403) {
          console.error('WebhookService: Access forbidden (403). Check webhook token permissions.');
          throw new Error(`Access forbidden: Invalid webhook token or insufficient permissions.`);
        } else if (response.status === 404) {
          console.error('WebhookService: Webhook not found (404). Check webhook URL configuration.');
          throw new Error(`Webhook not found: Check VITE_N8N_WEBHOOK_URL configuration.`);
        } else if (response.status === 500) {
          console.error('WebhookService: Internal server error (500). Check N8N workflow configuration.');
          throw new Error(`Internal server error: N8N workflow server error. Check workflow configuration.`);
        } else {
          throw new Error(`Webhook request failed: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      const result = await response.json();
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Webhook request timed out');
      }
      
      throw error;
    }
  }

  /**
   * Process historical emails for a user
   * Uses backend API for secure token decryption
   * @param {string} userId - User ID
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} Processing result
   */
  async processHistoricalEmails(userId, options = {}) {
    try {
      console.log('🔵 WebhookService: Starting processHistoricalEmails');
      console.log('🔵 WebhookService: User ID:', userId);
      
      // Use BackendApiService which handles proxy correctly (uses /api/email-scan in dev, full URL in prod)
      const result = await backendApiService.triggerEmailScan(userId, {
        processing_type: 'historical',
        use_test_webhook: false,
        lookback_days: options.lookbackDays || 730, // 2 years
        batch_size: options.batchSize || 50,
        ...options
      });
      
      console.log('✅ WebhookService: Historical processing triggered successfully!', result);
      return result;
    } catch (error) {
      console.error('❌ WebhookService: Error triggering historical email processing:', error);
      throw error;
    }
  }

  /**
   * Process daily emails for a user
   * Uses backend API for secure token decryption
   * @param {string} userId - User ID
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} Processing result
   */
  async processDailyEmails(userId, options = {}) {
    try {
      console.log('WebhookService: Triggering daily email processing via backend API...');
      
      // Use BackendApiService which handles proxy correctly (uses /api/email-scan in dev, full URL in prod)
      const result = await backendApiService.triggerEmailScan(userId, {
        processing_type: 'daily_sync',
        use_test_webhook: false,
        lookback_days: options.lookbackDays || 2, // Last 48 hours - provides buffer for missed syncs
        batch_size: options.batchSize || 50,
        ...options
      });
      
      console.log('WebhookService: Daily processing triggered successfully:', result);
      return result;
    } catch (error) {
      console.error('WebhookService: Error triggering daily email processing:', error);
      throw error;
    }
  }

  /**
   * Check webhook health and connectivity
   * @returns {Promise<Object>} Health status
   */
  async checkN8NWebhookHealth() {
    try {
      if (!this.isConfigured()) {
        return {
          status: 'error',
          message: 'Webhook not configured',
          configured: false
        };
      }

      const webhookUrl = getWebhookUrlForService();
      const secret = this.webhookSecret;
      
      if (!secret) {
        throw new Error('Webhook secret not configured');
      }
      
      console.log('🔍 Running health check with correct payload...');
      console.log('Webhook URL:', webhookUrl);
      console.log('Secret preview:', secret ? `${secret.substring(0, 10)}...` : 'NOT SET');
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...createBasicAuthHeader(secret)
        },
        body: JSON.stringify({
          user_id: "health-check-user",
          email_address: "healthcheck@example.com",
          access_token: "health-check-token",
          processing_type: "test"
        })
      });
      
      const responseText = await response.text();
      
      console.log('📊 Health check with correct payload:', {
        status: response.status,
        ok: response.ok,
        response: responseText
      });
      
      if (response.ok) {
        return {
          status: 'healthy',
          message: 'Webhook is responding with correct payload',
          configured: true,
          response: responseText
        };
      } else {
        return {
          status: 'error',
          message: `Webhook returned ${response.status}: ${responseText}`,
          configured: true
        };
      }
      
    } catch (error) {
      console.error('💥 Health check failed:', error);
      
      return {
        status: 'error',
        message: error.message,
        configured: this.isConfigured()
      };
    }
  }

  /**
   * Test webhook with a simple ping
   * @returns {Promise<Object>} Test result
   */
  async testWebhook() {
    try {
      const payload = {
        // REQUIRED FIELDS - All four fields must be present for N8N workflow
        user_id: "test-user",
        email_address: "test@example.com",
        access_token: "test-token",
        processing_type: "test",
        
        // Additional test fields
        action: 'test',
        timestamp: new Date().toISOString(),
        test_data: {
          message: 'Test ping from CampConnect',
          version: '1.0.0'
        }
      };

      const result = await this.sendWebhookRequest(payload);
      
      return {
        success: true,
        message: 'Webhook test successful',
        response: result
      };
    } catch (error) {
      console.error('Webhook test failed:', error);
      
      return {
        success: false,
        message: error.message,
        error: error
      };
    }
  }

  /**
   * Get processing status for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Processing status
   */
  async getProcessingStatus(userId) {
    try {
      // Get processing status from old table (processing fields not in JSONB schema)
      const { data: connection, error } = await supabase
        .from('user_email_connections')
        .select('processing_status, last_processing_date, last_error')
        .eq('user_id', userId)
        .eq('provider', 'gmail')
        .single();

      if (error || !connection) {
        return {
          status: 'not_connected',
          message: 'No email connection found'
        };
      }

      return {
        status: connection.processing_status || 'idle',
        lastProcessing: connection.last_processing_date,
        lastError: connection.last_error,
        isProcessing: connection.processing_status === 'processing'
      };
    } catch (error) {
      console.error('Error getting processing status:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Retry failed processing
   * @param {string} userId - User ID
   * @param {string} type - Type of processing to retry ('daily' or 'historical')
   * @returns {Promise<Object>} Retry result
   */
  async retryProcessing(userId, type = 'daily') {
    try {
      // Clear any previous error status (keep in old table for processing status tracking)
      await supabase
        .from('user_email_connections')
        .update({ 
          processing_status: 'retrying',
          last_error: null
        })
        .eq('user_id', userId);

      // Retry the appropriate processing type
      if (type === 'historical') {
        return await this.processHistoricalEmails(userId);
      } else {
        return await this.processDailyEmails(userId);
      }
    } catch (error) {
      console.error('Error retrying processing:', error);
      throw error;
    }
  }
}

// Create singleton instance
const webhookService = new N8NWebhookService();

// Export individual functions for convenience
export const processHistoricalEmails = (userId, options) => 
  webhookService.processHistoricalEmails(userId, options);

export const processDailyEmails = (userId, options) => 
  webhookService.processDailyEmails(userId, options);

export const checkN8NWebhookHealth = () => 
  webhookService.checkN8NWebhookHealth();

export const testWebhook = () => 
  webhookService.testWebhook();

export const getProcessingStatus = (userId) => 
  webhookService.getProcessingStatus(userId);

// Add to webhookService.js for debugging
export const diagnoseWebhookIssue = async () => {
  const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
  const webhookSecret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  console.log('🔍 Diagnosing N8N Webhook Issue...');
  console.log('Webhook URL:', webhookUrl);
  console.log('Webhook Secret:', webhookSecret ? `${webhookSecret.substring(0, 10)}...` : 'NOT SET');
  
  try {
    // Test 1: Basic connectivity (no auth)
    console.log('Test 1: Basic GET request...');
    const basicResponse = await fetch(webhookUrl, {
      method: 'GET', // Try GET first
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('Basic GET Response:', {
      status: basicResponse.status,
      statusText: basicResponse.statusText,
      headers: Object.fromEntries(basicResponse.headers.entries())
    });
    
    // Test 2: POST without auth
    console.log('Test 2: POST without authentication...');
    const postResponse = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        test: true,
        timestamp: new Date().toISOString()
      })
    });
    
    const postResponseText = await postResponse.text();
    console.log('POST Response (no auth):', {
      status: postResponse.status,
      statusText: postResponse.statusText,
      responseText: postResponseText
    });
    
    // Test 3: Check if webhook secret is required (Authorization Bearer - PRIMARY METHOD)
    if (webhookSecret) {
      console.log('Test 3: POST with Authorization Bearer header (CORRECT FORMAT - no space)...');
      const authResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...createBasicAuthHeader(webhookSecret)
        },
        body: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString()
        })
      });
      
      const authResponseText = await authResponse.text();
      console.log('POST Response (Authorization Bearer):', {
        status: authResponse.status,
        statusText: authResponse.statusText,
        responseText: authResponseText
      });
      
      // Test 4: Try X-API-Key header (alternative method)
      console.log('Test 4: POST with X-API-Key header...');
      const apiKeyResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': webhookSecret
        },
        body: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString()
        })
      });
      
      const apiKeyResponseText = await apiKeyResponse.text();
      console.log('POST Response (X-API-Key):', {
        status: apiKeyResponse.status,
        statusText: apiKeyResponse.statusText,
        responseText: apiKeyResponseText
      });
      
      // Test 5: Try X-Webhook-Secret header
      console.log('Test 5: POST with X-Webhook-Secret header...');
      const secretResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Secret': webhookSecret
        },
        body: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString()
        })
      });
      
      const secretResponseText = await secretResponse.text();
      console.log('POST Response (with X-Webhook-Secret):', {
        status: secretResponse.status,
        statusText: secretResponse.statusText,
        responseText: secretResponseText
      });
      
      // Test 6: Try query parameter
      console.log('Test 6: POST with secret as query parameter...');
      const queryUrl = `${webhookUrl}?secret=${webhookSecret}`;
      const queryResponse = await fetch(queryUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test: true,
          timestamp: new Date().toISOString()
        })
      });
      
      const queryResponseText = await queryResponse.text();
      console.log('POST Response (with query param):', {
        status: queryResponse.status,
        statusText: queryResponse.statusText,
        responseText: queryResponseText
      });
    }
    
    console.log('🔍 Diagnostic complete!');
    
  } catch (error) {
    console.error('Diagnostic Error:', error);
  }
};

// Test the authenticated webhook
export const testAuthenticatedWebhook = async () => {
  const webhookUrl = getWebhookUrlForService();
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  if (!secret) {
    console.error('❌ VITE_N8N_WEBHOOK_SECRET not set');
    return false;
  }
  
  if (!webhookUrl) {
    console.error('❌ Webhook URL not available');
    return false;
  }
  
  console.log('🧪 Testing authenticated webhook...');
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Webhook URL:', webhookUrl);
  console.log('Secret:', secret ? `${secret.substring(0, 10)}...` : 'NOT SET');
  
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...createBasicAuthHeader(secret)
      },
      body: JSON.stringify({
        user_id: "test-user",
        email_address: "test@example.com",
        access_token: "test-token",
        processing_type: "test"
      })
    });
    
    const responseText = await response.text();
    console.log('✅ Authenticated webhook test:', {
      status: response.status,
      statusText: response.statusText,
      response: responseText
    });
    
    const success = response.status === 200;
    if (success) {
      console.log('🎉 Webhook authentication test PASSED!');
    } else {
      console.log('❌ Webhook authentication test FAILED!');
    }
    
    return success;
    
  } catch (error) {
    console.error('❌ Webhook test failed:', error);
    return false;
  }
};

// Add to webhookService.js for debugging
export const debugAuthConfiguration = () => {
  const webhookUrl = getWebhookUrlForService();
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  console.log('🔍 Auth Configuration Debug:');
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Webhook URL:', webhookUrl);
  console.log('Secret exists:', !!secret);
  console.log('Secret length:', secret ? secret.length : 0);
  console.log('Secret preview:', secret ? `${secret.substring(0, 8)}...` : 'NOT SET');
  
  // Check if secret has Bearer prefix
  if (secret && secret.startsWith('Bearer ')) {
    console.warn('⚠️ Secret includes "Bearer" - this might cause double Bearer issue');
  }
  
  // Check if secret is the old placeholder value
  if (secret === 'your_webhook_secret_here' || secret === 'f47ac10b58cc4372a5670e02b2c3d479') {
    console.warn('⚠️ Secret appears to be a placeholder or old value - update with new secure secret');
  }
  
  // Check if URL is placeholder
  if (webhookUrl === 'your_webhook_url_here' || !webhookUrl) {
    console.warn('⚠️ Webhook URL is not properly configured');
  }
  
  return { webhookUrl, secret };
};

// Test multiple authentication formats
export const testAllAuthFormats = async () => {
  const webhookUrl = getWebhookUrlForService();
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  if (!secret) {
    console.error('❌ No webhook secret found');
    return null;
  }
  
  if (!webhookUrl) {
    console.error('❌ No webhook URL found');
    return null;
  }
  
  const testPayload = {
    user_id: "test-user",
    email_address: "test@example.com", 
    access_token: "test-token",
    processing_type: "test"
  };
  
  const authFormats = [
    { name: 'Basic Auth (CORRECT)', headers: createBasicAuthHeader(secret) },
    { name: 'Bearer with space (DEPRECATED)', headers: { 'Authorization': `Bearer ${secret}` } },
    { name: 'Just secret', headers: { 'Authorization': secret } },
    { name: 'X-API-Key header', headers: { 'X-API-Key': secret } },
    { name: 'Custom header', headers: { 'X-Webhook-Secret': secret } },
    { name: 'Basic auth format', headers: { 'Authorization': `Basic ${btoa(secret)}` } }
  ];
  
  console.log('🧪 Testing all authentication formats...');
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Webhook URL:', webhookUrl);
  console.log('Secret preview:', `${secret.substring(0, 8)}...`);
  console.log('Testing', authFormats.length, 'different formats...\n');
  
  for (const format of authFormats) {
    try {
      console.log(`Testing: ${format.name}`);
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...format.headers
        },
        body: JSON.stringify(testPayload)
      });
      
      const responseText = await response.text();
      
      console.log(`${format.name} Result:`, {
        status: response.status,
        statusText: response.statusText,
        response: responseText.substring(0, 200) // First 200 chars
      });
      
      if (response.status === 200) {
        console.log('🎉 SUCCESS! Use this format:', format.name);
        console.log('Headers:', format.headers);
        console.log('✅ This is the working authentication method!');
        return format;
      }
      
    } catch (error) {
      console.log(`${format.name} Error:`, error.message);
    }
    
    console.log(''); // Add spacing between tests
  }
  
  console.log('❌ All authentication formats failed');
  console.log('💡 Check your N8N webhook configuration and secret');
  return null;
};

// Test CORS specifically
export const testCORS = async () => {
  const webhookUrl = getWebhookUrlForService();
  
  if (!webhookUrl) {
    console.error('❌ No webhook URL found');
    return;
  }
  
  console.log('🌐 Testing CORS with n8n webhook...');
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Webhook URL:', webhookUrl);
  console.log('Origin:', window.location.origin);
  
  try {
    // Test OPTIONS request (preflight)
    console.log('Testing OPTIONS request (CORS preflight)...');
    const optionsResponse = await fetch(webhookUrl, {
      method: 'OPTIONS',
      headers: {
        'Origin': window.location.origin,
        'Access-Control-Request-Method': 'POST',
        'Access-Control-Request-Headers': 'Content-Type, Authorization'
      }
    });
    
    const optionsHeaders = Object.fromEntries(optionsResponse.headers.entries());
    console.log('OPTIONS Response:', {
      status: optionsResponse.status,
      statusText: optionsResponse.statusText,
      headers: optionsHeaders
    });
    
    // Check for CORS headers
    if (optionsHeaders['access-control-allow-origin']) {
      console.log('✅ CORS Origin allowed:', optionsHeaders['access-control-allow-origin']);
    } else {
      console.log('⚠️ No Access-Control-Allow-Origin header found');
    }
    
    if (optionsHeaders['access-control-allow-methods']) {
      console.log('✅ CORS Methods allowed:', optionsHeaders['access-control-allow-methods']);
    } else {
      console.log('⚠️ No Access-Control-Allow-Methods header found');
    }
    
    if (optionsHeaders['access-control-allow-headers']) {
      console.log('✅ CORS Headers allowed:', optionsHeaders['access-control-allow-headers']);
    } else {
      console.log('⚠️ No Access-Control-Allow-Headers header found');
    }
    
    // Test actual POST request
    console.log('\nTesting POST request...');
    const postResponse = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin
      },
      body: JSON.stringify({
        user_id: "cors-test",
        email_address: "test@example.com",
        access_token: "test-token", 
        processing_type: "test"
      })
    });
    
    const postResponseText = await postResponse.text();
    console.log('POST Response:', {
      status: postResponse.status,
      statusText: postResponse.statusText,
      text: postResponseText.substring(0, 200) // First 200 chars
    });
    
    // Check if CORS blocked the request
    if (postResponse.status === 0) {
      console.log('❌ CORS Error: Request was blocked by CORS policy');
    } else if (postResponse.status >= 200 && postResponse.status < 300) {
      console.log('✅ POST request successful - CORS is working');
    } else {
      console.log('⚠️ POST request failed, but not due to CORS (status:', postResponse.status, ')');
    }
    
  } catch (error) {
    console.error('❌ CORS Test Error:', error);
    
    // Check if it's a CORS error
    if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
      console.log('🚫 This appears to be a CORS error');
    } else if (error.message.includes('Failed to fetch')) {
      console.log('🌐 Network error - could be CORS or connectivity issue');
    }
  }
};

// Test function for Cursor to implement
export const testCompleteFlow = async () => {
  console.log('Testing complete webhook flow...');
  
  const webhookUrl = getWebhookUrlForService(); // Should use proxy in dev
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  if (!secret) {
    console.error('❌ VITE_N8N_WEBHOOK_SECRET not set');
    return false;
  }
  
  if (!webhookUrl) {
    console.error('❌ Webhook URL not available');
    return false;
  }
  
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Webhook URL:', webhookUrl);
  console.log('Secret preview:', secret ? `${secret.substring(0, 10)}...` : 'NOT SET');
  console.log('Testing with Bearer authentication (no space)...');
  
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...createBasicAuthHeader(secret) // No space!
      },
      body: JSON.stringify({
        user_id: "final-test",
        email_address: "test@example.com",
        access_token: "test-token",
        processing_type: "historical"
      })
    });
    
    const responseText = await response.text();
    console.log('Final test result:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      response: responseText.substring(0, 200) // First 200 chars
    });
    
    if (response.ok) {
      console.log('🎉 WEBHOOK WORKING PERFECTLY!');
      console.log('✅ Proxy URL working:', import.meta.env.DEV ? 'Yes (development)' : 'N/A (production)');
      console.log('✅ Bearer authentication working: Yes (no space)');
      console.log('✅ Complete flow successful!');
      return true;
    } else {
      console.log('❌ Webhook request failed with status:', response.status);
      console.log('Response:', responseText);
    }
    
  } catch (error) {
    console.error('Final test error:', error);
    console.log('❌ Network or CORS error occurred');
  }
  
  return false;
};

// Test proxy specifically
export const testProxyConnection = async () => {
  console.log('🔧 Testing proxy connection...');
  
  const webhookUrl = getWebhookUrlForService();
  console.log('Using URL:', webhookUrl);
  
  if (!webhookUrl.startsWith('/api/webhook')) {
    console.error('❌ Not using proxy URL in development!');
    return false;
  }
  
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer${import.meta.env.VITE_N8N_WEBHOOK_SECRET}`
      },
      body: JSON.stringify({
        test: 'proxy-connection',
        timestamp: new Date().toISOString()
      })
    });
    
    console.log('Proxy test result:', {
      status: response.status,
      ok: response.ok,
      url: webhookUrl
    });
    
    if (response.ok) {
      console.log('🎉 PROXY WORKING! CORS bypassed successfully!');
      return true;
    } else {
      console.log('⚠️ Proxy connected but webhook returned error:', response.status);
      return false;
    }
    
  } catch (error) {
    console.error('❌ Proxy test failed:', error);
    return false;
  }
};

// Add this enhanced test function
export const testProxyWithFullLogging = async () => {
  const testUrl = getWebhookUrl(false); // Use production webhook URL
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  console.log('🔍 Testing proxy with full request details...');
  console.log('URL:', testUrl);
  console.log('Secret exists:', !!secret);
  console.log('Secret length:', secret?.length);
  console.log('Secret preview:', secret ? `${secret.substring(0, 8)}...` : 'NOT SET');
  
  try {
    const response = await fetch(testUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...createBasicAuthHeader(secret)
      },
      body: JSON.stringify({
        user_id: "debug-test-user",
        email_address: "debug@example.com",
        access_token: "debug-test-token",
        processing_type: "historical"
      })
    });
    
    // Get response details
    const responseText = await response.text();
    const responseHeaders = Object.fromEntries(response.headers.entries());
    
    console.log('📊 Full Response Details:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: responseHeaders,
      body: responseText
    });
    
    // Try to parse error details
    try {
      const jsonResponse = JSON.parse(responseText);
      console.log('📋 Parsed Response:', jsonResponse);
    } catch (e) {
      console.log('📄 Response is not JSON:', responseText);
    }
    
    // Log success/failure
    if (response.ok) {
      console.log('🎉 PROXY TEST SUCCESSFUL!');
    } else {
      console.log('❌ PROXY TEST FAILED - Status:', response.status);
    }
    
  } catch (error) {
    console.error('💥 Request failed:', error);
    console.log('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
  }
};

// The same test should now work
export const testAfterN8NFix = async () => {
  console.log('🧪 Testing after N8N UUID fix...');
  
  const secret = import.meta.env.VITE_N8N_WEBHOOK_SECRET;
  
  if (!secret) {
    console.error('❌ VITE_N8N_WEBHOOK_SECRET not set');
    return false;
  }
  
  const webhookUrl = getWebhookUrl(false); // Use production webhook URL
  console.log('Using webhook URL:', webhookUrl);
  console.log('Secret preview:', secret ? `${secret.substring(0, 10)}...` : 'NOT SET');
  
  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...createBasicAuthHeader(secret)
      },
      body: JSON.stringify({
        user_id: "test-after-fix",
        email_address: "test@example.com",
        access_token: "test-token",
        processing_type: "historical"
      })
    });
    
    const responseText = await response.text();
    
    console.log('📊 Test after N8N fix:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      response: responseText
    });
    
    // Try to parse the response
    try {
      const jsonResponse = JSON.parse(responseText);
      console.log('📋 Parsed response:', jsonResponse);
    } catch (e) {
      console.log('📄 Response is not JSON:', responseText);
    }
    
    if (response.ok) {
      console.log('🎉 N8N WORKFLOW WORKING!');
      console.log('✅ Proxy connection successful');
      console.log('✅ Bearer authentication working');
      console.log('✅ N8N workflow processing requests');
      return true;
    } else {
      console.log('❌ N8N workflow still has issues - Status:', response.status);
      console.log('Response:', responseText);
    }
    
  } catch (error) {
    console.error('💥 Test failed:', error);
    console.log('Error details:', {
      name: error.name,
      message: error.message
    });
  }
  
  return false;
};

// Add comprehensive OAuth debugging
export const debugGmailOAuth = () => {
  console.log('🔍 Gmail OAuth Configuration Debug:');
  console.log('Google Client ID:', import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID);
  console.log('Client ID exists:', !!import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID);
  console.log('Client ID length:', import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID?.length);
  
  // Check if running in development
  console.log('Environment:', import.meta.env.DEV ? 'Development' : 'Production');
  console.log('Current URL:', window.location.href);
  
  // Check required scopes
  const requiredScopes = [
    'https://www.googleapis.com/auth/gmail.readonly',
    'openid',
    'profile', 
    'email'
  ];
  console.log('Required scopes:', requiredScopes);
  
  // Check OAuth configuration
  const oauthConfig = {
    clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
    redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || getOAuthRedirectUri(),
    scopes: import.meta.env.VITE_GOOGLE_SCOPES?.split(' ') || requiredScopes
  };
  
  console.log('OAuth Configuration:', oauthConfig);
  
  // Validate configuration
  const issues = [];
  if (!oauthConfig.clientId) {
    issues.push('❌ VITE_GOOGLE_OAUTH_CLIENT_ID not set');
  }
  if (!oauthConfig.redirectUri) {
    issues.push('❌ VITE_GOOGLE_REDIRECT_URI not set');
  }
  if (!oauthConfig.scopes || oauthConfig.scopes.length === 0) {
    issues.push('❌ VITE_GOOGLE_SCOPES not set');
  }
  
  if (issues.length > 0) {
    console.log('🚨 Configuration Issues:');
    issues.forEach(issue => console.log(issue));
  } else {
    console.log('✅ OAuth configuration appears valid');
  }
  
  // Check for common issues
  console.log('🔍 Common Issue Checks:');
  
  // Check if client ID looks like a placeholder
  if (oauthConfig.clientId?.includes('your_google_oauth_client_id_here')) {
    console.log('⚠️ Client ID appears to be a placeholder value');
  }
  
  // Check redirect URI format
  if (oauthConfig.redirectUri && !oauthConfig.redirectUri.startsWith('http')) {
    console.log('⚠️ Redirect URI should start with http:// or https://');
  }
  
  // Log current configuration for debugging
  logCurrentConfig();
  
  return {
    config: oauthConfig,
    issues: issues,
    isValid: issues.length === 0
  };
};

export const retryProcessing = (userId, type) => 
  webhookService.retryProcessing(userId, type);

export default webhookService;
